/**
 * Web Push registration.
 *
 * Requires a secure context, so this quietly does nothing over plain HTTP on a
 * LAN — the app still works, it just will not wake in the background there.
 */
import type { VavClient } from '@vav/client-core';

export type PushState = 'unsupported' | 'insecure' | 'denied' | 'off' | 'on';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function currentPushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  // localhost counts as secure; a LAN IP over http does not.
  if (!window.isSecureContext) return 'insecure';
  if (Notification.permission === 'denied') return 'denied';

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? 'on' : 'off';
}

/** Register the worker early so a push can arrive even before the user opts in. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return undefined;
  try {
    /**
     * Whether a worker is already in charge, captured *before* registering.
     *
     * A new worker calls `clients.claim()`, which fires `controllerchange` on
     * first install as well as on every update. Reloading on the first install
     * would reload every new visitor for no reason; reloading on an update is
     * the entire point. The difference is only visible from here, before the
     * registration happens.
     */
    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloading = false;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || reloading) return;
      reloading = true;
      // An installed app opens whatever its worker has cached, so without this
      // a shipped fix can sit on the server while the user keeps running the
      // old bundle and reasonably concludes it was never fixed.
      window.location.reload();
    });

    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (err) {
    console.warn('[vav] service worker registration failed', err);
    return undefined;
  }
}

export async function enableWebPush(client: VavClient): Promise<PushState> {
  const state = await currentPushState();
  if (state === 'unsupported' || state === 'insecure' || state === 'denied') return state;

  const config = await client.pushConfig();
  if (!config.enabled || !config.web) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!registration) return 'unsupported';

  const subscription = await registration.pushManager.subscribe({
    // Chrome refuses anything else: every push must be able to show a
    // notification, which is why the worker always shows one when hidden.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.web.vapidPublicKey),
  });

  await client.enablePush('web', JSON.stringify(subscription));
  return 'on';
}

/**
 * Register the browser's single subscription against every profile.
 *
 * A browser has one push subscription per origin, which looks at first like a
 * limit of one notifiable account. It is not: the server's `push_tokens` table
 * is keyed `(user_id, device_id)` and its index on the token is not unique, so
 * the same endpoint can belong to as many accounts as you like. Whichever one
 * receives a message, the server wakes this endpoint, and the page drains all
 * of them.
 *
 * Takes clients rather than doing the subscribing itself so the permission
 * prompt stays where the user asked for it -- exactly one, on the active
 * profile -- and the rest simply reuse what it produced.
 */
export async function shareSubscriptionWith(clients: VavClient[]): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const token = JSON.stringify(subscription);
  await Promise.all(
    clients.map((client) =>
      // One profile failing to register must not cost the others theirs.
      client.enablePush('web', token).catch(() => {}),
    ),
  );
}

export async function disableWebPush(client: VavClient): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe();
  await client.disablePush().catch(() => {});
  return 'off';
}

/**
 * VAPID keys are distributed base64url; PushManager wants raw bytes.
 *
 * Returns an ArrayBuffer rather than a view — TypeScript's DOM types insist on
 * a buffer that is definitely not shared, and a plain Uint8Array does not
 * satisfy that.
 */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const normalised = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return buffer;
}
